import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { useToast } from './Toast';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import { SIGNATURES_KEY, mailErrorMessage, useSignatures, type UserSignature } from '../lib/mail';

/**
 * SignaturesEditor — the signed-in user's email signatures (spec 0014, AC-7).
 *
 * One or more signatures, one default (used when the composer does not pick another). The first
 * can start from the firm's signature template, filled with the user's name, title, email, phone
 * and firm. The body is HTML; the preview renders it sanitized.
 *
 *   GET/POST /api/users/me/signatures · PUT/DELETE /api/users/me/signatures/:id
 *   POST /api/users/me/signatures/seed · GET /api/users/me/signatures/template
 */
export function SignaturesEditor() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const q = useSignatures();
  const [editing, setEditing] = useState<{ id: string | null; name: string; body_html: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const list = q.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY });

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    try { await fn(); await refresh(); toast({ variant: 'success', message: done }); return true; } catch (err) {
      toast({ variant: 'error', message: mailErrorMessage(err) });
      return false;
    } finally { setBusy(false); }
  }

  async function startNew() {
    try {
      const t = await api.get<{ body_html: string }>('/api/users/me/signatures/template');
      setEditing({ id: null, name: list.length ? '' : 'Default', body_html: t.body_html });
    } catch { setEditing({ id: null, name: '', body_html: '' }); }
  }
  async function save() {
    if (!editing) return;
    const body = { name: editing.name, body_html: editing.body_html };
    const ok = await run(
      () => (editing.id ? api.put(`/api/users/me/signatures/${editing.id}`, body) : api.post('/api/users/me/signatures', body)),
      'Signature saved.',
    );
    if (ok) setEditing(null);
  }

  const card = { border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-3)', background: 'var(--color-card)' } as const;
  const hint = { fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', margin: 0 } as const;

  return (
    <div data-testid="signatures-editor" style={{ display: 'grid', gap: 'var(--sp-3)' }}>
      {q.data && list.length === 0 && !editing && (
        <div style={{ ...card, display: 'grid', gap: 'var(--sp-2)' }}>
          <p style={hint}>You have no signature yet. Start from your firm&apos;s template, filled in with your details, then adjust it.</p>
          <div><Button variant="primary" size="sm" disabled={busy} onClick={() => run(() => api.post('/api/users/me/signatures/seed', {}), 'Signature created from the firm template.')}>Create from firm template</Button></div>
        </div>
      )}

      {list.map((s: UserSignature) => (
        <div key={s.id} style={{ ...card, display: 'grid', gap: 'var(--sp-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <strong>{s.name}</strong>
            {s.is_default && <Badge variant="info">Default</Badge>}
            <span style={{ flex: 1 }} />
            {!s.is_default && <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(() => api.put(`/api/users/me/signatures/${s.id}`, { is_default: true }), `${s.name} is now your default.`)}>Make default</Button>}
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing({ id: s.id, name: s.name, body_html: s.body_html })}>Edit</Button>
            <Button variant="ghost" size="sm" disabled={busy} aria-label={`Delete ${s.name}`} onClick={() => run(() => api.delete(`/api/users/me/signatures/${s.id}`), 'Signature deleted.')}>Delete</Button>
          </div>
          <div style={{ fontSize: 'var(--fs-sm)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.body_html) }} />
        </div>
      ))}

      {editing ? (
        <div style={{ ...card, display: 'grid', gap: 'var(--sp-2)' }}>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>Name</span>
            <Input aria-label="Signature name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Default" />
          </label>
          <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>Signature (HTML)</span>
            <textarea aria-label="Signature HTML" rows={6} value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
              style={{ width: '100%', fontFamily: 'var(--ff-mono, monospace)', fontSize: 'var(--fs-xs)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)' }} />
          </label>
          <span style={hint}>Preview</span>
          <div data-testid="signature-preview" style={{ border: '1px dashed var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(editing.body_html) }} />
          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save} loading={busy} disabled={!editing.name.trim()}>Save signature</Button>
          </div>
        </div>
      ) : (
        list.length > 0 && <div><Button variant="secondary" size="sm" onClick={startNew} disabled={busy}>Add a signature</Button></div>
      )}
    </div>
  );
}
